import { isInMsTeamsAuthWrapper, useAuth } from "@healthos/auth/react";
import { cn } from "@healthos/ui";
import { SignOut } from "@phosphor-icons/react";
import { NavLink } from "react-router-dom";
import { DEFAULT_NAV_ITEMS, type DefaultNavItem } from "./nav-items";

export interface BottomNavProps {
  items?: DefaultNavItem[];
  className?: string;
}

export function BottomNav({ items = DEFAULT_NAV_ITEMS, className }: BottomNavProps) {
  const { state, signOut } = useAuth();
  const showSignOut = state.status === "signedIn" && !isInMsTeamsAuthWrapper();
  const visibleItems = items.filter((item) => !item.hideOnMobile).slice(0, showSignOut ? 4 : 5);

  return (
    <nav
      data-app-chrome="bottom-nav"
      aria-label="Primary"
      className={cn(
        "md:hidden fixed bottom-0 left-0 right-0 z-40 flex justify-center pointer-events-none",
        className,
      )}
    >
      <div className="w-full max-w-md pointer-events-auto">
        <div
          className="mx-3 mb-3 rounded-xl bg-white/90 backdrop-blur-md border border-mist"
          style={{
            paddingBottom: "env(safe-area-inset-bottom)",
            boxShadow: "0 8px 30px color-mix(in srgb, var(--color-teal-700) 8%, transparent)",
          }}
        >
          <ul className={cn("grid", gridCols(visibleItems.length + (showSignOut ? 1 : 0)))}>
            {visibleItems.map(
              ({ id, label, compactLabel, path, Icon, end, disableViewTransition, badge }) => (
                <li key={id}>
                  <NavLink
                    to={path}
                    end={end ?? (path === "/" || path === "/operations")}
                    viewTransition={!disableViewTransition}
                    aria-label={badge && badge > 0 ? `${label}, ${badge} unread` : undefined}
                    className="w-full flex flex-col items-center justify-center gap-0.5 py-2 md:py-3 transition-colors"
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          className={cn(
                            "relative flex items-center justify-center w-9 h-8 rounded-full [transition:background-color_180ms_var(--ease-out-expo),color_180ms_var(--ease-out-expo)]",
                            isActive ? "bg-leaf-soft text-teal-700" : "text-ink-teal",
                          )}
                        >
                          <Icon size={18} weight={isActive ? "fill" : "regular"} />
                          {badge && badge > 0 ? (
                            <span
                              aria-hidden="true"
                              className="absolute -top-0.5 -right-1 min-w-4 rounded-full bg-teal-700 px-1 text-center font-mono text-2xs font-medium leading-4 tabular-nums text-white ring-2 ring-white"
                            >
                              {badge}
                            </span>
                          ) : null}
                        </span>
                        <span
                          className={cn(
                            "text-2xs font-medium tracking-tight leading-tight text-center px-0.5 transition-colors",
                            isActive ? "text-teal-700" : "text-ink-teal",
                          )}
                        >
                          {compactLabel ?? label}
                        </span>
                      </>
                    )}
                  </NavLink>
                </li>
              ),
            )}
            {showSignOut ? (
              <li>
                <button
                  type="button"
                  onClick={() => signOut()}
                  className="w-full flex flex-col items-center justify-center gap-0.5 py-2 md:py-3 transition-colors"
                >
                  <span className="flex items-center justify-center w-9 h-8 rounded-full text-ink-teal hover:text-teal-700 transition-colors">
                    <SignOut size={18} weight="regular" />
                  </span>
                  <span className="text-2xs font-medium tracking-tight leading-tight text-center px-0.5 text-ink-teal">
                    Sign out
                  </span>
                </button>
              </li>
            ) : null}
          </ul>
        </div>
      </div>
    </nav>
  );
}

function gridCols(n: number): string {
  switch (n) {
    case 2:
      return "grid-cols-2";
    case 3:
      return "grid-cols-3";
    case 4:
      return "grid-cols-4";
    case 5:
      return "grid-cols-5";
    case 6:
      return "grid-cols-6";
    default:
      return "grid-cols-6";
  }
}
